import moment from 'moment';
import { Constants } from '../constants';
import { OperatingHours } from '../models';
import { parseTimeToMoment } from '../validators';

export class DateUtils {
  private static readonly EST_OFFSET_MINUTES = -5 * 60;

  static toUTC(date: string, outputFormat: 'YYYY/MM/DD' | 'hh:mm A' | 'YYYY/MM/DD hh:mm A' | 'yyyy-MM-DDTHH:mm:ss.SSS+0000' | 'yyyy-MM-DDTHH:mm:ss'): string {
    return moment(date).subtract(DateUtils.EST_OFFSET_MINUTES, 'minutes').format(outputFormat);
  }

  static toLocal(date: string | undefined, inputFormat = 'yyyy-MM-DDTHH:mm:ss.SSS+0000', outputFormat = 'yyyy-MM-DDTHH:mm:ss'): string {
    if (!date) {
      return '';
    }
    return moment(date, inputFormat).add(DateUtils.EST_OFFSET_MINUTES, 'minutes').format(outputFormat);
  }

  static getDateTime(date: string, outputFormat: 'MM/DD/YYYY' | 'hh:mm A' | 'YYYY/MM/DD hh:mm A'): Date {
    return moment(date, outputFormat).toDate();
  }

  static getDateTimeString(date: string | undefined, outputFormat: 'MM/DD/YYYY' | 'hh:mm A' | 'YYYY/MM/DD hh:mm A'): string {
    if (!date) {
      return '';
    }
    return moment(date, outputFormat).toString();
  }

  static getUtcRangeForLocalDate(dateStr: Date | string): { startUtc: string; endUtc: string } {
    if (!dateStr) return { startUtc: '', endUtc: '' };

    const m = moment(dateStr).utcOffset(DateUtils.EST_OFFSET_MINUTES, true);

    const startUtc = m.clone().startOf('day').utc().format('yyyy-MM-DDTHH:mm:ss');

    const endUtc = m.clone().endOf('day').utc().format('yyyy-MM-DDTHH:mm:ss');

    return { startUtc, endUtc };
  }

  static getNextValidStartDate(scheduleDate: string | Date, daysOfSchedule: Array<number> | undefined, dateFormat = 'yyyy-MM-DD'): string {
    if (!scheduleDate) {
      return '';
    }

    const start = moment(scheduleDate);

    if (!daysOfSchedule || !daysOfSchedule.length) {
      return start.format(dateFormat);
    }

    const validDays = daysOfSchedule.map((d) => Number(d)).filter((d) => Number.isInteger(d) && d >= 0 && d <= 6);

    if (!validDays.length) {
      return start.format(dateFormat);
    }

    if (validDays.includes(start.day())) {
      return start.format(dateFormat);
    }

    for (let i = 1; i <= 7; i++) {
      const candidate = start.clone().add(i, 'days');
      if (validDays.includes(candidate.day())) {
        return candidate.format(dateFormat);
      }
    }

    return start.format(dateFormat);
  }

  static nowInSchoolTimeZone(): moment.Moment {
    return moment().utcOffset(DateUtils.EST_OFFSET_MINUTES);
  }

  static getNextActionDue(nextActionDate: string | undefined): { label: string; daysAway: number } | null {
    if (!nextActionDate) {
      return null;
    }

    const due = moment(DateUtils.toLocal(nextActionDate));
    const time = due.format('h:mm A');
    const today = moment(DateUtils.nowInSchoolTimeZone().format('yyyy-MM-DDTHH:mm:ss')).startOf('day');
    const daysAway = due.clone().startOf('day').diff(today, 'days');

    if (daysAway < 0) {
      return { label: `Overdue - ${due.format('MMM D')}, ${time}`, daysAway };
    }
    if (daysAway === 0) {
      return { label: `Today, ${time}`, daysAway };
    }
    if (daysAway === 1) {
      return { label: `Tomorrow, ${time}`, daysAway };
    }
    return { label: `${due.format('MMM D, YYYY')}, ${time}`, daysAway };
  }

  static getCurrentDateTime(): string {
    return new Date().toISOString();
  }

  static getCurrentDateTimeInESTandUTC(): string {
    return this.toUTC(moment().utcOffset(DateUtils.EST_OFFSET_MINUTES, true).format('yyyy-MM-DDTHH:mm:ss'), 'yyyy-MM-DDTHH:mm:ss');
  }

  static buildTimeOptions(hours: OperatingHours | undefined, after = '', gapMinutes = 15): Array<string> {
    if (!hours) {
      return [];
    }
    const earliest = after ? parseTimeToMoment(after) : undefined;
    const end = parseTimeToMoment(hours.end);
    const options: Array<string> = [];
    for (const time = parseTimeToMoment(hours.start); !time.isAfter(end); time.add(gapMinutes, 'minutes')) {
      if (!earliest?.isValid() || time.isAfter(earliest)) {
        options.push(time.format(Constants.dateFormats.h_mm_A));
      }
    }
    return options;
  }
}
